import {
  Box,
  ButtonSpinner,
  Flex,
  Menu,
  MenuButton,
  MenuGroup,
  MenuItem,
  MenuList,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { faBuilding, faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Router } from '@ui/config/routes';
import { theme } from '@ui/config/theme';
import { useCurrentUser } from '@ui/hooks/useCurrentUser';
import { scrollbarCSS } from '@ui/util/css';
import { hyphenateIfDifferent } from '@ui/util/hyphenate-if-different';
import { menuProps } from '@ui/util/menuProps';
import { Link } from 'react-router';

import { useNavigationContext } from './context';

type SiteSelectorProps = {
  teamCode?: string;
  isPending?: boolean;
};

export function TeamSelector({
  teamCode,
  isPending = false,
}: SiteSelectorProps) {
  const { isOpen } = useNavigationContext();
  const { currentUser } = useCurrentUser();

  if (currentUser.teams.length === 0) {
    return null;
  }

  const teams = currentUser.teams;

  return (
    <Menu>
      <MenuButton
        width="100%"
        height="50px"
        opacity={0.72}
        fontSize={16}
        borderWidth="1px"
        borderColor="rgba(255, 255, 255, 0.4)"
        borderRadius={8}
        _hover={{
          opacity: 1,
          borderColor: 'rgba(255, 255, 255, 0.7)',
        }}
        _disabled={{
          opacity: 0.5,
          borderColor: 'rgba(255, 255, 255, 0.4)',
        }}
        disabled={isPending}
      >
        <Flex
          alignItems="center"
          justifyContent={isOpen ? 'flex-start' : 'center'}
        >
          <Tooltip label="Sites" placement="right" isDisabled={false}>
            {isPending ? (
              <Flex w="36px" justifyContent="center">
                <ButtonSpinner position="relative" />
              </Flex>
            ) : (
              <FontAwesomeIcon
                style={{ margin: 0 }}
                icon={faBuilding}
                width={36}
              />
            )}
          </Tooltip>

          {isOpen && (
            <Text
              fontWeight="semibold"
              fontFamily="navigationItem"
              opacity={isOpen ? 1 : 0}
              maxWidth={isOpen ? 'unset' : 0}
              maxHeight={isOpen ? 'unset' : 0}
              marginLeft={isOpen ? 3 : 0}
            >
              {teamCode ? teamCode : 'Select team...'}
            </Text>
          )}
        </Flex>
      </MenuButton>
      <MenuList
        borderRadius={0}
        color="mw.lightGrey"
        backgroundColor="mw.darkGrey"
        p="1.5rem"
        border="none"
      >
        <MenuGroup title="Current Site">
          <Box
            maxHeight="225px"
            overflow="auto"
            css={scrollbarCSS({
              background: theme.colors.mw.darkGrey,
              thumb: theme.colors.mw.lightGrey,
            })}
          >
            {teams.map((team) => {
              const teamUuid = String(team.uuid);
              return (
                <MenuItem
                  key={teamUuid}
                  value={teamUuid}
                  {...menuProps.item}
                  p={0}
                >
                  <Flex
                    as={Link}
                    to={Router.teamPath(
                      team.teamCode,
                      Router.routes.quoteQueue,
                    )}
                    p="12px"
                    _hover={{ textDecoration: 'none' }}
                  >
                    <Box width="24px">
                      {team.teamCode === teamCode && (
                        <FontAwesomeIcon icon={faCheck} />
                      )}
                    </Box>

                    <Box width="90px" fontWeight="bold" marginLeft={2}>
                      {hyphenateIfDifferent(team.site.code, team.teamCode)}
                    </Box>

                    <Box
                      width="225px"
                      whiteSpace="nowrap"
                      textOverflow="ellipsis"
                      overflow="hidden"
                    >
                      {hyphenateIfDifferent(team.site.name, team.teamName)}
                    </Box>
                  </Flex>
                </MenuItem>
              );
            })}
          </Box>
        </MenuGroup>
      </MenuList>
    </Menu>
  );
}
